// Pure builder for the Risk Assessment PDF as a pdfmake document definition.
// Produces REAL VECTOR TEXT (selectable + searchable), replacing the raster
// image PDF html2pdf used to produce from RiskAssessmentPrintLayout.js. This
// doc backs the unified "Download / Print" button (SharePdfButton) for RAs.
//
// No dependency on pdfmake itself — only declarative doc-def data is returned,
// so the layout stays pure and unit-testable (no font VFS, no DOM, no canvas
// imperatively). Mirrors the conventions in orderPdf.js / budgetReportPdf.js.
//
// SPEC SOURCE: client/src/components/RiskAssessmentPrintLayout.js — this file
// reproduces that layout section-by-section as pdfmake.

import {
  computeHrn,
  bandFor,
  BANDS_WITH_ACTIONS,
  needsReduction,
  parseControlsByStage,
  CONTROL_STAGES,
  PPE_CATALOGUE,
  parsePpeList,
  REGS_CATALOGUE,
  parseRegsList,
  SITE_NOTICES_CATALOGUE,
  parseNoticesList,
} from '../constants/riskAssessment';
import { parseActivity, ACTIVITY_HEADINGS } from './activityFormat';
import { PRE_REVIEW_QUESTIONS, POST_REVIEW_QUESTIONS } from '../constants/raReviewQuestions';
// Static emoji → PNG map (pre-generated; a missing key falls back to a text label).
import { RA_EMOJI_PNG } from '../constants/raEmojiImages';

// ── Palette / shared style tokens ──────────────────────────────────────────
const NAVY = '#1e3a5f';
const GREY_RULE = '#d1d5db';
const SECONDARY_FG = '#666666';
const FOOTER_GREY = '#999999';
const HEADER_GREY_BG = '#f3f4f6';

// Band swatch / pill colours — copied verbatim from RiskAssessmentPrintLayout.
const BAND_BG = {
  green: '#dcfce7',
  yellow: '#fef9c3',
  orange: '#fed7aa',
  red: '#fecaca',
};
const BAND_FG = {
  green: '#166534',
  yellow: '#854d0e',
  orange: '#9a3412',
  red: '#991b1b',
};

// Control-stage badge colours — copied verbatim from PrintControls.
const STAGE_COLOURS = {
  1: { bg: '#d1fae5', fg: '#065f46' }, // emerald
  2: { bg: '#dbeafe', fg: '#1e3a8a' }, // sky
  3: { bg: '#fef3c7', fg: '#92400e' }, // amber
};

// A4 landscape printable width ≈ 842 − (16 + 16) margins ≈ 810. Kept just under
// the content width so the canvas rule never overflows.
const HEADER_LINE_WIDTH = 806;

// ── Date helper ────────────────────────────────────────────────────────────
// Mirrors RiskAssessmentPrintLayout.formatPrintDate — en-GB 2-digit/short/numeric,
// '—' for empty, raw passthrough for un-parseable strings.
function formatPrintDate(s) {
  if (!s) return '—';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const footerDate = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

// ── Shared table layouts ───────────────────────────────────────────────────
// Thin grey-ruled bordered table (meta + guide tables + sign-off).
const borderedLayout = {
  hLineColor: () => GREY_RULE,
  vLineColor: () => GREY_RULE,
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => 4,
  paddingRight: () => 4,
};

// Per-hazard "card" layout — thin grey rules, a touch more horizontal padding
// than the meta tables so the 3-column middle row breathes.
const cardLayout = {
  hLineColor: () => GREY_RULE,
  vLineColor: () => GREY_RULE,
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  paddingTop: () => 4,
  paddingBottom: () => 4,
  paddingLeft: () => 5,
  paddingRight: () => 5,
};

// ── Small reusable nodes ───────────────────────────────────────────────────
function navyHeading(text, size = 12, margin = [0, 0, 0, 4]) {
  return { text, fontSize: size, bold: true, color: NAVY, margin };
}

function navyHeaderCell(text, align = 'left') {
  return { text, fillColor: NAVY, color: 'white', bold: true, fontSize: 8.5, alignment: align };
}

function greyLabelCell(text) {
  return { text, fillColor: HEADER_GREY_BG, bold: true, fontSize: 9 };
}

// HRN cell — factor line + band-coloured pill. Mirrors HrnCell.
function hrnCell(lo, fe, dph, np) {
  const hrn = computeHrn(lo, fe, dph, np);
  const band = bandFor(hrn);
  const bg = BAND_BG[band.tone];
  const fg = BAND_FG[band.tone];
  const display = hrn < 10 ? Math.round(hrn * 100) / 100 : Math.round(hrn);
  const pillText = `HRN ${display} · ${band.label}${needsReduction(hrn) ? ' — Reduction recommended' : ''}`;
  return {
    stack: [
      {
        text: [
          { text: 'LO ', color: SECONDARY_FG }, { text: String(lo) },
          { text: ' · FE ', color: SECONDARY_FG }, { text: String(fe) },
          { text: ' · DPH ', color: SECONDARY_FG }, { text: String(dph) },
          { text: ' · NP ', color: SECONDARY_FG }, { text: String(np) },
        ],
        fontSize: 8,
      },
      {
        table: {
          widths: ['auto'],
          body: [[{ text: pillText, fillColor: bg, color: fg, bold: true, fontSize: 8.5 }]],
        },
        layout: {
          hLineWidth: () => 0, vLineWidth: () => 0,
          paddingTop: () => 1, paddingBottom: () => 1, paddingLeft: () => 4, paddingRight: () => 4,
        },
        margin: [0, 2, 0, 0],
      },
    ],
  };
}

// Controls cell — per-stage coloured bar + bulleted items spread across
// `colCount` columns (row-major). Full page width allows 3 columns.
function controlsCell(value, colCount = 2) {
  const stages = parseControlsByStage(value);
  const hasAny = CONTROL_STAGES.some((s) => (stages[s.n] || []).length > 0);
  if (!hasAny) {
    return { text: 'No controls recorded.', italics: true, color: '#9ca3af', fontSize: 8 };
  }
  const groups = [];
  let rendered = 0;
  CONTROL_STAGES.forEach((s) => {
    const items = stages[s.n] || [];
    if (items.length === 0) return;
    const col = STAGE_COLOURS[s.n];
    // Full-width coloured bar spanning the Controls column — acts as a divider
    // between stages. A little top gap before every bar except the first.
    groups.push({
      table: {
        widths: ['*'],
        body: [[{ text: `Stage ${s.n} — ${s.label}`, fillColor: col.bg, color: col.fg, bold: true, fontSize: 7 }]],
      },
      layout: {
        hLineWidth: () => 0, vLineWidth: () => 0,
        paddingTop: () => 1, paddingBottom: () => 1, paddingLeft: () => 4, paddingRight: () => 4,
      },
      margin: [0, rendered > 0 ? 4 : 0, 0, 2],
    });
    rendered += 1;
    const cols = Math.min(colCount, items.length) || 1;
    if (cols === 1) {
      groups.push({
        ul: items.map((b) => ({ text: String(b), fontSize: 8 })),
        margin: [4, 0, 0, 3],
      });
    } else {
      // Spread measures across `cols` bullet columns, row-major (i % cols), so
      // several measures share a line.
      const buckets = Array.from({ length: cols }, () => []);
      items.forEach((b, i) => buckets[i % cols].push({ text: String(b), fontSize: 8 }));
      groups.push({
        columns: buckets.map((bk) => ({ width: '*', ul: bk })),
        columnGap: 14,
        margin: [4, 0, 0, 3],
      });
    }
  });
  return { stack: groups };
}

// ── Emoji image node + strip ───────────────────────────────────────────────
// emojiNode(item) → icon-over-label stack if a PNG exists, else a text label.
function emojiNode(item) {
  const png = item && item.emoji ? RA_EMOJI_PNG[item.emoji] : null;
  if (png) {
    return {
      width: 'auto',
      stack: [
        { image: png, width: 18, alignment: 'center' },
        { text: item.label, fontSize: 7, alignment: 'center', margin: [0, 2, 0, 0] },
      ],
      margin: [0, 0, 10, 6],
    };
  }
  return {
    width: 'auto',
    text: item.label,
    fontSize: 8,
    margin: [0, 0, 10, 6],
  };
}

// Wrapping row of emoji/label nodes (PPE or site notices).
function emojiStrip(items) {
  if (!items.length) return null;
  return {
    columns: items.map((it) => emojiNode(it)),
    columnGap: 4,
    margin: [0, 2, 0, 0],
  };
}

// ── Footer band legend + disclaimer ────────────────────────────────────────
// bandSwatches() returns the inline row of HRN band swatches ("HRN bands: …")
// as an array of column nodes, so it can be dropped into the footer's print
// row alongside the print/page text rather than on a line of its own.
function bandSwatches() {
  const labelSize = 7;
  const swatches = [{ width: 'auto', text: 'HRN bands:', bold: true, fontSize: labelSize, color: '#555555', margin: [0, 0, 8, 0] }];
  BANDS_WITH_ACTIONS.forEach((b) => {
    swatches.push({
      width: 'auto',
      columns: [
        {
          // True 6×6 square via canvas — a table cell would stretch tall to fit
          // the space character's line height.
          width: 6,
          canvas: [{ type: 'rect', x: 0, y: 0, w: 6, h: 6, lineWidth: 0.5, lineColor: BAND_FG[b.tone], color: BAND_BG[b.tone] }],
          margin: [0, 2, 0, 0],
        },
        { width: 'auto', text: `${b.range} ${b.label}`, fontSize: labelSize, color: '#555555', margin: [3, 0, 8, 0] },
      ],
      columnGap: 0,
    });
  });
  return swatches;
}

// One-line footer disclaimer reminder — the full disclaimer has its own page.
const FOOTER_DISCLAIMER =
  'Guide only — the user / operator must verify every control suits the actual site, '
  + 'equipment and current legislation before work begins. Full terms on the disclaimer '
  + 'page; Crew Forge accepts no liability for omissions or use beyond the scope assessed.';

// ── Review questionnaire renderer (pre / post) ─────────────────────────────
// Mirrors PrintReviewSection: gate question controls follow-up visibility,
// per-question label/question/answer-pill/no-outcome/notes, plus any
// noBottomNotice amber boxes for questions answered 'no'.
function reviewSection(title, questions, answers) {
  const safe = answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {};
  const gate = questions[0];
  const rest = questions.slice(1);
  const gateAnswer = safe[gate.key] ? (safe[gate.key].answer ?? null) : null;
  const showRestOn = gate.showRestOn || 'yes';
  const visible = gateAnswer === showRestOn ? [gate, ...rest] : [gate];

  const labelFor = (a) => (a === 'yes' ? 'Yes' : a === 'no' ? 'No' : '—');
  const pillColours = (a) => ({
    bg: a === 'yes' ? '#d1fae5' : a === 'no' ? '#fee2e2' : '#e5e7eb',
    fg: a === 'yes' ? '#065f46' : a === 'no' ? '#991b1b' : '#374151',
  });

  const body = visible.map((q) => {
    const a = safe[q.key] ? (safe[q.key].answer ?? null) : null;
    const note = (safe[q.key] && safe[q.key].note) || '';
    const pc = pillColours(a);
    const valueStack = [
      { text: q.question, fontSize: 9, margin: [0, 0, 0, 3] },
      {
        table: { widths: ['auto'], body: [[{ text: labelFor(a), fillColor: pc.bg, color: pc.fg, bold: true, fontSize: 8.5 }]] },
        layout: {
          hLineWidth: () => 0, vLineWidth: () => 0,
          paddingTop: () => 1, paddingBottom: () => 1, paddingLeft: () => 5, paddingRight: () => 5,
        },
      },
    ];
    if (a === 'no') {
      const positive = q.noTone === 'positive';
      valueStack.push({
        table: { widths: ['*'], body: [[{ text: q.noOutcome, fontSize: 8.5, color: positive ? '#065f46' : '#92400e' }]] },
        layout: {
          fillColor: () => (positive ? '#d1fae5' : '#fef3c7'),
          hLineColor: () => (positive ? '#a7f3d0' : '#fde68a'),
          vLineColor: () => (positive ? '#a7f3d0' : '#fde68a'),
          hLineWidth: () => 0.5, vLineWidth: () => 0.5,
          paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 5, paddingRight: () => 5,
        },
        margin: [0, 3, 0, 0],
      });
    }
    if (note) {
      valueStack.push({
        text: [
          { text: 'Notes: ', italics: true, color: '#6b7280' },
          { text: String(note) },
        ],
        fontSize: 8.5, color: '#374151', preserveLeadingSpaces: true, margin: [0, 3, 0, 0],
      });
    }
    return [
      { ...greyLabelCell(q.label) },
      { stack: valueStack },
    ];
  });

  const out = [
    navyHeading(title),
  ];
  if (gate.intro) {
    out.push({ text: gate.intro, fontSize: 9, color: SECONDARY_FG, margin: [0, 0, 0, 6] });
  }
  out.push({
    table: { widths: ['22%', '*'], body },
    layout: borderedLayout,
    margin: [0, 0, 0, 6],
  });

  // Bottom notices for any noBottomNotice question answered 'no'.
  questions
    .filter((q) => q.noBottomNotice && safe[q.key] && safe[q.key].answer === 'no')
    .forEach((q) => {
      out.push({
        table: {
          widths: ['*'],
          body: [[{
            stack: [
              { text: q.noBottomNotice.title, bold: true, fontSize: 9, color: '#78350f', margin: [0, 0, 0, 2] },
              { text: q.noBottomNotice.body, fontSize: 8.5, color: '#78350f' },
            ],
          }]],
        },
        layout: {
          fillColor: () => '#fef3c7',
          hLineColor: () => '#fcd34d', vLineColor: () => '#fcd34d',
          hLineWidth: () => 0.5, vLineWidth: () => 0.5,
          paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: () => 8, paddingRight: () => 8,
        },
        margin: [0, 0, 0, 6],
      });
    });

  return { stack: out, margin: [0, 12, 0, 12] };
}

// ── Cover page ─────────────────────────────────────────────────────────────
function coverPage(ra) {
  const out = [];

  out.push({
    columns: [
      {
        width: 'auto',
        text: `Risk Assessment${ra.reference ? ` · ${ra.reference}` : ''}`,
        fontSize: 17, bold: true, color: NAVY,
      },
      {
        width: '*',
        text: [
          { text: ra.title, bold: true, color: '#333333' },
          {
            text: ` · Version ${ra.version}`
              + (ra.status === 'signed_off' ? ' · SIGNED OFF' : ' · DRAFT')
              + (ra.archived_at ? ' · ARCHIVED' : ''),
            color: SECONDARY_FG,
          },
        ],
        fontSize: 12, margin: [8, 5, 0, 0],
      },
    ],
    columnGap: 0,
    margin: [0, 0, 0, 8],
  });

  out.push(navyHeading('How to read this assessment'));
  out.push({
    text: [
      'Follows ', { text: 'EN ISO 12100:2010', bold: true }, ' + ',
      { text: 'ISO/TR 14121-2', bold: true },
      ' (HRN scoring) — the same methodology used by machinery designers, notified bodies, '
      + 'and HSE inspectors. Each hazard is identified, scored before controls, mitigated via '
      + 'the three-stage hierarchy (Eliminate → Engineer → Inform / PPE), then re-scored to '
      + 'demonstrate residual risk is As Low As Reasonably Practicable (ALARP). Hazard Rating '
      + 'Number: ',
      { text: 'HRN = LO × FE × DPH × NP', bold: true },
      '.',
    ],
    fontSize: 9, margin: [0, 0, 0, 6], lineHeight: 1.35,
  });

  // HRN factors table.
  const factorRows = [
    [navyHeaderCell('Factor'), navyHeaderCell('What it measures'), navyHeaderCell('Range')],
    [{ text: 'LO', bold: true }, { text: 'Likelihood of Occurrence' }, { text: '0.033 (Almost impossible) → 15 (Certain)' }],
    [{ text: 'FE', bold: true }, { text: 'Frequency of Exposure' }, { text: '0.5 (Annually) → 5 (Constantly)' }],
    [{ text: 'DPH', bold: true }, { text: 'Degree of Possible Harm' }, { text: '0.1 (Scratch) → 15 (Fatality)' }],
    [{ text: 'NP', bold: true }, { text: 'Number of Persons at risk' }, { text: '1 (1–2 persons) → 12 (50+ persons)' }],
  ];
  out.push({
    table: { headerRows: 1, widths: ['10%', '40%', '*'], body: factorRows },
    layout: borderedLayout,
    fontSize: 8.5,
    margin: [0, 0, 0, 6],
  });

  out.push(navyHeading('Risk bands', 10, [0, 8, 0, 3]));
  const bandRows = [
    [navyHeaderCell('Band'), navyHeaderCell('HRN range'), navyHeaderCell('Suggested action')],
  ];
  BANDS_WITH_ACTIONS.forEach((b) => {
    bandRows.push([
      {
        columns: [
          {
            width: 12,
            table: { widths: [10], heights: [10], body: [[{ text: ' ', fillColor: BAND_BG[b.tone] }]] },
            layout: {
              hLineColor: () => BAND_FG[b.tone], vLineColor: () => BAND_FG[b.tone],
              hLineWidth: () => 0.5, vLineWidth: () => 0.5,
              paddingTop: () => 0, paddingBottom: () => 0, paddingLeft: () => 0, paddingRight: () => 0,
            },
          },
          { width: '*', text: b.label, bold: true, color: BAND_FG[b.tone], margin: [5, 0, 0, 0] },
        ],
        columnGap: 0,
      },
      { text: b.range },
      { text: b.suggestedAction },
    ]);
  });
  out.push({
    table: { headerRows: 1, widths: ['30%', '20%', '*'], body: bandRows },
    layout: borderedLayout,
    fontSize: 8.5,
    margin: [0, 0, 0, 6],
  });

  // Applicable regulations (left column) + PPE required (right column) —
  // placed side by side to save vertical space on the cover.
  const regSlugs = parseRegsList(ra.regs_list);
  const ppeSlugs = parsePpeList(ra.ppe_list);

  const regColumn = regSlugs.length > 0
    ? {
        stack: [
          navyHeading('Applicable regulations for this Risk Assessment', 10, [0, 8, 0, 3]),
          {
            ul: REGS_CATALOGUE.filter((r) => regSlugs.includes(r.slug)).map((r) => ({
              text: [{ text: r.short, bold: true }, { text: ` — ${r.long}` }],
              fontSize: 8.5,
              margin: [0, 0, 0, 2],
            })),
            margin: [0, 0, 0, 4],
          },
        ],
      }
    : null;

  const ppeColumn = ppeSlugs.length > 0
    ? {
        stack: [
          navyHeading('PPE required across this Risk Assessment', 10, [0, 8, 0, 3]),
          emojiStrip(PPE_CATALOGUE.filter((p) => ppeSlugs.includes(p.slug))) || { text: '' },
        ],
      }
    : null;

  if (regColumn && ppeColumn) {
    out.push({
      columns: [
        { width: '*', stack: regColumn.stack },
        { width: '*', stack: ppeColumn.stack },
      ],
      columnGap: 24,
    });
  } else if (regColumn) {
    out.push(regColumn);
  } else if (ppeColumn) {
    out.push(ppeColumn);
  }

  // Site notices.
  const noticeSlugs = parseNoticesList(ra.notices);
  if (noticeSlugs.length > 0) {
    const noticeItems = SITE_NOTICES_CATALOGUE.filter((n) => noticeSlugs.includes(n.slug));
    out.push(navyHeading('Site notices for this Risk Assessment', 10, [0, 8, 0, 3]));
    const strip = emojiStrip(noticeItems);
    if (strip) out.push(strip);
  }

  // End the cover on its own page — hazards start on page 2.
  out.push({ text: '', pageBreak: 'after' });
  return out;
}

// ── Compact header (page 2+) ───────────────────────────────────────────────
function compactHeader(ra) {
  return [
    {
      columns: [
        { width: 'auto', text: ra.title, fontSize: 16, bold: true, color: NAVY },
        {
          width: '*',
          text:
            `${ra.reference ? `Ref ${ra.reference} · ` : ''}Version ${ra.version}`
            + (ra.status === 'signed_off' ? ' · SIGNED OFF' : ' · DRAFT')
            + (ra.archived_at ? ' · ARCHIVED' : ''),
          fontSize: 9, color: SECONDARY_FG, margin: [8, 7, 0, 0],
        },
      ],
      columnGap: 0,
    },
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: HEADER_LINE_WIDTH, y2: 0, lineWidth: 2, lineColor: NAVY }], margin: [0, 6, 0, 12] },
  ];
}

// ── Metadata table ─────────────────────────────────────────────────────────
function metadataTable(ra) {
  const rows = [];
  rows.push([
    greyLabelCell('Location'), { text: ra.location || '—' },
    greyLabelCell('Assessor'), { text: ra.assessor_name || '—' },
  ]);

  const parts = parseActivity(ra.activity);
  const nonEmpty = ACTIVITY_HEADINGS.filter((h) => (parts[h.key] || '').trim());
  if (nonEmpty.length === 0) {
    rows.push([
      greyLabelCell('Activity'),
      { text: '—', colSpan: 3 }, {}, {},
    ]);
  } else {
    nonEmpty.forEach((h) => {
      rows.push([
        greyLabelCell(h.label),
        { text: parts[h.key], colSpan: 3, preserveLeadingSpaces: true }, {}, {},
      ]);
    });
  }

  rows.push([
    greyLabelCell('Assessment date'), { text: formatPrintDate(ra.assessment_date) },
    greyLabelCell('Review date'), { text: formatPrintDate(ra.review_date) },
  ]);

  return {
    table: { widths: ['15%', '35%', '15%', '35%'], body: rows },
    layout: borderedLayout,
    fontSize: 9,
    margin: [0, 0, 0, 12],
  };
}

// ── Hazard sections ────────────────────────────────────────────────────────
// Tiny uppercase column label sitting above each cell of a card (replaces the
// old shared table header row now that every hazard is its own stacked card).
function miniLabel(text) {
  return { text, fontSize: 6.5, bold: true, color: SECONDARY_FG, margin: [0, 0, 0, 2] };
}

// One hazard rendered as a compact stacked card:
//   Row 1 (full width)  · # + hazard name (left) + who is affected (right)
//   Row 2 (3 columns)   · Initial HRN | Controls (elastic) | Residual HRN
//   Row 3 (full width)  · Notes — only when present
function hazardCard(r, idx, fallbackName) {
  const headerCell = {
    colSpan: 3,
    fillColor: HEADER_GREY_BG,
    columns: [
      {
        width: '*',
        text: [
          { text: `${idx + 1}.  `, bold: true, color: NAVY },
          { text: r.hazard || fallbackName || '', bold: true, color: NAVY },
        ],
        fontSize: 10,
      },
      {
        width: 'auto',
        text: [
          { text: 'Affects: ', color: SECONDARY_FG },
          { text: r.who_affected || '—', color: '#333333' },
        ],
        fontSize: 8.5,
        alignment: 'right',
        margin: [10, 1, 0, 0],
      },
    ],
  };

  const middleRow = [
    { stack: [miniLabel('INITIAL HRN'), hrnCell(r.lo_init, r.fe_init, r.dph_init, r.np_init)] },
    { stack: [miniLabel('CONTROLS'), controlsCell(r.controls, 2)] },
    { stack: [miniLabel('RESIDUAL HRN'), hrnCell(r.lo_res, r.fe_res, r.dph_res, r.np_res)] },
  ];

  const body = [
    [headerCell, {}, {}],
    middleRow,
  ];

  if ((r.notes || '').trim()) {
    body.push([
      {
        colSpan: 3,
        text: [
          { text: 'Notes: ', bold: true, color: SECONDARY_FG },
          { text: r.notes, color: '#333333' },
        ],
        fontSize: 8.5,
        preserveLeadingSpaces: true,
      },
      {}, {},
    ]);
  }

  return {
    unbreakable: true,
    table: {
      // '*' on Controls (elastic) — Initial/Residual HRN take just enough width
      // for the factor line (e.g. "LO 5 · FE 2.5 · DPH 15 · NP 4"), freeing the
      // bulk of the width for the two-column staged controls list.
      widths: ['18%', '*', '18%'],
      body,
    },
    layout: cardLayout,
    fontSize: 8.5,
    margin: [0, 0, 0, 6],
  };
}

// Renders one section's hazards as a run of cards. No section heading — each
// hazard's title lives in its own card header (the row hazard is the instance
// title; section.name is just the library-block origin and would duplicate it).
// `startIndex` lets card numbers run sequentially across the whole document.
function hazardSection(section, startIndex) {
  const rows = section.rows || [];
  if (rows.length === 0) return null;
  const cards = rows.map((r, i) => hazardCard(r, startIndex + i, section.name));
  return {
    stack: cards,
    margin: [0, 0, 0, 14],
  };
}

// ── Sign-off block ─────────────────────────────────────────────────────────
function signOffBlock(ra) {
  const out = [navyHeading('Sign-off')];
  if (ra.signatures && ra.signatures.length > 0) {
    const body = [
      [navyHeaderCell('Signer'), navyHeaderCell('Role'), navyHeaderCell('Date')],
    ];
    ra.signatures.forEach((s) => {
      body.push([
        { text: s.signer_name || '', bold: true },
        { text: s.signer_role || '—' },
        { text: formatPrintDate(s.signed_at) },
      ]);
    });
    out.push({
      table: { headerRows: 1, widths: ['40%', '40%', '20%'], body },
      layout: borderedLayout,
      fontSize: 9,
    });
  } else {
    const body = [
      [navyHeaderCell('Signer name'), navyHeaderCell('Role'), navyHeaderCell('Date')],
    ];
    for (let i = 0; i < 3; i++) {
      body.push([
        { text: ' ', margin: [0, 6, 0, 6] },
        { text: ' ', margin: [0, 6, 0, 6] },
        { text: ' ', margin: [0, 6, 0, 6] },
      ]);
    }
    out.push({
      table: { headerRows: 1, widths: ['40%', '40%', '20%'], body },
      layout: borderedLayout,
      fontSize: 9,
    });
  }
  return { stack: out, margin: [0, 16, 0, 0] };
}

// ── Disclaimer page ────────────────────────────────────────────────────────
function disclaimerPage() {
  return {
    stack: [
      { text: 'User responsibility & disclaimer', fontSize: 18, bold: true, color: NAVY, margin: [0, 0, 0, 8] },
      {
        text: [
          'This assessment was generated by Crew Forge from a library of standard hazards and '
          + 'was authored by the named assessor on the cover. It is intended as a professional '
          + 'starting point — ',
          { text: 'not a substitute for site-specific verification', bold: true },
          '. Before any work commences:',
        ],
        fontSize: 10, margin: [0, 0, 0, 8], lineHeight: 1.5,
      },
      {
        ul: [
          { text: ['The named assessor / responsible supervisor must ', { text: 'verify every listed control is in place', bold: true }, ', suitable for the actual site conditions, equipment, and personnel.'] },
          { text: ['The user must check the document is the ', { text: 'current signed-off version', bold: true }, ' — superseded versions are not valid.'] },
          { text: ['Any change in scope, equipment, location, or personnel triggers a ', { text: 'review', bold: true }, ' — see review date on cover.'] },
          { text: ['The end user / operator is ultimately ', { text: 'responsible', bold: true }, ' for safe operation; this document does not transfer that responsibility.'] },
          { text: ['Crew Forge and the hazard library it ships with are provided ', { text: 'as-is, without warranty', bold: true }, '. The library is not exhaustive and is not a substitute for competent advice from a qualified safety professional where the situation demands one.'] },
        ],
        fontSize: 10, lineHeight: 1.5,
        margin: [0, 0, 0, 10],
      },
    ],
    pageBreak: 'before',
    margin: [0, 8, 0, 0],
  };
}

// ── Sign-on sheet ──────────────────────────────────────────────────────────
function signOnSheet(ra) {
  const header = [
    navyHeaderCell('#'),
    navyHeaderCell('Print Name'),
    navyHeaderCell('Signature'),
    navyHeaderCell('Department / Role'),
    navyHeaderCell('Date'),
  ];
  const body = [header];
  for (let i = 0; i < 20; i++) {
    body.push([
      { text: String(i + 1), alignment: 'center', color: '#888888', margin: [0, 5, 0, 5] },
      { text: ' ', margin: [0, 5, 0, 5] },
      { text: ' ', margin: [0, 5, 0, 5] },
      { text: ' ', margin: [0, 5, 0, 5] },
      { text: ' ', margin: [0, 5, 0, 5] },
    ]);
  }
  return {
    stack: [
      { text: 'Briefing Sign-On Sheet', fontSize: 18, bold: true, color: NAVY, margin: [0, 0, 0, 4] },
      {
        text: `${ra.title}${ra.reference ? ` · Ref ${ra.reference}` : ''} · Version ${ra.version}`,
        fontSize: 11, color: '#333333', margin: [0, 0, 0, 4],
      },
      {
        text: [
          { text: 'I confirm I have been briefed on the contents of this Risk Assessment and understand the controls in place.', bold: true },
          { text: ' By signing below I acknowledge the hazards, control measures, PPE requirements, and site notices listed in this document.' },
        ],
        fontSize: 9.5, color: '#444444', margin: [0, 0, 0, 12], lineHeight: 1.45,
      },
      {
        table: {
          headerRows: 1,
          widths: ['4%', '26%', '26%', '24%', '*'],
          body,
        },
        layout: {
          fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 1 ? '#fafafa' : null),
          hLineColor: () => GREY_RULE,
          vLineColor: () => GREY_RULE,
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          paddingTop: () => 3,
          paddingBottom: () => 3,
          paddingLeft: () => 4,
          paddingRight: () => 4,
        },
        fontSize: 9.5,
      },
      {
        text: 'Retain this page with the production safety file once all crew have signed.',
        fontSize: 7.5, color: '#555555', italics: true, alignment: 'center', margin: [0, 10, 0, 0],
      },
    ],
    pageBreak: 'before',
    margin: [0, 8, 0, 0],
  };
}

// ── Inner content (no page wrapper) ────────────────────────────────────────
// Returns just the pdfmake content[] slice for a Risk Assessment. Pure + testable.
export function buildRiskAssessmentContent(ra, { includeSignOnSheet = false, includeReviewQuestions = false } = {}) {
  if (!ra) return [];
  const sections = ra.sections || [];

  const content = [];

  // 1. Cover page.
  content.push(...coverPage(ra));

  // 2. Compact header.
  content.push(...compactHeader(ra));

  // 3. Metadata table.
  content.push(metadataTable(ra));

  // 4. Pre-review (optional).
  if (includeReviewQuestions) {
    content.push(reviewSection('Pre-Assessment Review', PRE_REVIEW_QUESTIONS, ra.pre_review_answers));
  }

  // 5. Hazard sections — cards numbered sequentially across the document.
  let hazardNo = 0;
  sections.forEach((section) => {
    content.push(hazardSection(section, hazardNo));
    hazardNo += (section.rows || []).length;
  });

  // 6. Post-review (optional).
  if (includeReviewQuestions) {
    content.push(reviewSection('Residual Risk Review', POST_REVIEW_QUESTIONS, ra.post_review_answers));
  }

  // 7. Sign-off.
  content.push(signOffBlock(ra));

  // 8. Closing attribution line (the band legend + disclaimer now render once
  //    per page via the document footer, not inline under each section).
  content.push({
    text: 'Generated by Crew Forge · HRN scoring per ISO/TR 14121-2',
    fontSize: 7, color: '#888888', alignment: 'center', margin: [0, 6, 0, 0],
  });

  // 9. Disclaimer page.
  content.push(disclaimerPage());

  // 10. Sign-on sheet (optional).
  if (includeSignOnSheet) {
    content.push(signOnSheet(ra));
  }

  return content.filter(Boolean);
}

// ── Full doc wrapper ───────────────────────────────────────────────────────
export function buildRiskAssessmentDoc(ra, opts = {}) {
  const safeRa = ra || {};
  const footerLabel = safeRa.reference ? `RA ${safeRa.reference}` : (safeRa.title || 'Risk Assessment');
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    // Tight side/top margins for max usable width; bottom leaves room for the
    // compact 2-row per-page footer (1-line disclaimer + print/bands/page row).
    pageMargins: [16, 16, 16, 34],
    info: {
      title: safeRa.title || 'Risk Assessment',
      author: 'Crew Forge',
      subject: 'Risk Assessment',
    },
    // Footer renders once at the bottom of every page: a one-line disclaimer,
    // then a single row carrying the print line, the HRN band legend, and the
    // page number together.
    footer: (currentPage, pageCount) => ({
      stack: [
        { text: FOOTER_DISCLAIMER, fontSize: 6, color: '#888888', italics: true, lineHeight: 1 },
        {
          columns: [
            {
              width: 'auto',
              text: `Printed ${footerDate()} | ${footerLabel} v${safeRa.version} | Crew Forge`,
              fontSize: 7, color: FOOTER_GREY,
              margin: [0, 1, 0, 0],
            },
            {
              width: '*',
              columns: bandSwatches(),
              columnGap: 4,
              margin: [12, 0, 12, 0],
            },
            {
              width: 'auto',
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 7, color: FOOTER_GREY, alignment: 'right',
              margin: [0, 1, 0, 0],
            },
          ],
          columnGap: 0,
          margin: [0, 2, 0, 0],
        },
      ],
      margin: [16, 4, 16, 0],
    }),
    defaultStyle: { fontSize: 9, color: '#000000' },
    content: buildRiskAssessmentContent(ra, opts),
  };
}
